'use client';

import { useEffect, useState } from 'react';
import {
  decodeReactError,
  reportClientError,
} from '@/lib/report-client-error';
import { clearSiteStateAndReload } from '@/lib/recovery';

/**
 * Route-group error boundary for the lobby / `/board/[boardId]` tree.
 *
 * Next.js automatically wraps every route in the group with this component
 * (app-router convention: `error.tsx` neighbours `layout.tsx`), so an
 * uncaught render-time throw anywhere inside `LobbyClient` no longer walks
 * the tree up to the root 500 page. That was the shape the user reported
 * on mobile: rapid board-switching could race a hook (e.g. when the URL
 * pointed at a board id the API didn't recognise) and the resulting
 * unhandled exception would break the whole app until a hard refresh.
 *
 * The UI is intentionally framework-free below this comment.
 *
 * History — why no `<Link>`, no `<Button asChild>`, no `<Slot>` here:
 * The recovery path used to render
 *   <Button asChild><Link href="/">Go home</Link></Button>
 *   <Button onClick={reset}>Retry</Button>
 * which on production (`takeover.fun`, four replicas) reliably reproduced a
 * `Maximum update depth exceeded` (React #185) cycle whose head was always
 * `setRef → composeRefs → setRef → React commit → throw` (see
 * `report-client-error.ts` payloads tagged `console.error.react-invariant`
 * captured against commit `ff9b0dc` and later). The trigger:
 *   1. `next@14.2.35` carries a known crash in
 *      `client/components/router-reducer/fill-lazy-items-till-leaf-with-head.ts:153`
 *      — `newCache.parallelRoutes.get(key)` throws `Cannot read properties
 *      of null (reading 'get')` when the framework's module-level
 *      `initialParallelRoutes` has been nulled by the `Router` cleanup
 *      effect *before* a subsequent re-run of `Router`'s
 *      `useMemo([buildId, initialSeedData, urlParts, initialTree, …])`.
 *      Tracked upstream as vercel/next.js#67730 and #69272; PR #75360
 *      shipped the fix in Next 15 only and was *not* backported to 14.x.
 *   2. We catch that throw here, the app-router mounts this fallback, and
 *      `<Link href="/">` immediately schedules a prefetch via the
 *      `useIntersection` observer (200px root margin). The prefetch goes
 *      through Next's router cache reducer, which re-runs the same
 *      now-corrupt `useMemo`, throws again, the fallback re-mounts, the
 *      `<Link>` prefetches again — loop. React's update-depth limiter
 *      eventually trips with #185, *not* the original `TypeError`, which
 *      is what made this look like a React invariant rather than a
 *      framework cache bug for so long.
 * The fix is structural: this fallback now uses a plain `<a href="/">`
 * (full-document navigation; resets every in-memory map including
 * Next's router cache) and a plain `<button>` with
 * `window.location.reload()` (also a full-document reload), so the
 * recovery path no longer depends on the still-broken Next router. Until
 * Next.js ships a 14.x backport (or we move to 15.x), keep this file
 * framework-free below this comment.
 *
 * Desktop hits this path too on a real crash, but users saw it "only on
 * mobile" because slow cellular latency made the race window easier to hit.
 */
export default function LobbyError({
  error,
  reset: _reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log a structured payload so the next reproduction lands every
    // useful piece of context next to the stack in DevTools — pathname
    // (was the URL pointing at a malformed board id?), digest (matches
    // the on-screen `ref` so support tickets and server logs can be
    // correlated), and the original error itself (Chrome / Safari
    // expand the stack inline when an Error object is logged).
    // Production Next.js obfuscates `error.message`, so the digest +
    // pathname pair is what the user-side log actually carries; the
    // dev build keeps the full message + stack inline.
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    console.error('[lobby/error-boundary]', {
      pathname,
      digest: error?.digest,
      name: error?.name,
      message: error?.message,
      error,
    });
    // Belt-and-braces: also POST to `/api/client-error`. The
    // `console.error` patch installed by `<ErrorTrap />` in
    // `app/layout.tsx` will *also* pick up the line above and
    // forward it (with the React `componentStack` it captured a
    // moment earlier on the same tick); the reporter de-dupes by
    // `(source|name|message|stack-head)` within a 5s window so the
    // two paths converge to a single server log line. We still call
    // directly here in case the trap failed to mount.
    reportClientError({
      source: 'lobby/error-boundary',
      error,
      digest: error?.digest ?? null,
    });
  }, [error]);

  const isDev = process.env.NODE_ENV !== 'production';
  // See `app/global-error.tsx` for the rationale on showing the
  // decoded React invariant + replica id in production. Same
  // pattern, slightly different visual chrome (Tailwind + ui kit).
  const decoded = decodeReactError(error?.message);
  const replicaShort =
    typeof document !== 'undefined'
      ? document
          .querySelector('meta[name="x-replica-short"]')
          ?.getAttribute('content') ?? 'unknown'
      : 'unknown';
  const replicaRegion =
    typeof document !== 'undefined'
      ? document
          .querySelector('meta[name="x-replica-region"]')
          ?.getAttribute('content') ?? 'unknown'
      : 'unknown';

  // Both controls trigger a full-document load (reload / `<a href>`).
  // We deliberately avoid `reset()` and `<Link>` because the router
  // cache that just threw is re-used by both — see the file header.
  const handleHardReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  // "Reset session & reload" hands the user the same remedy the
  // first reproducer applied manually from DevTools (the user-side
  // workaround that broke the production loop): clear all cookies +
  // local/session storage + IndexedDB, then full-reload. See
  // `src/lib/recovery.ts` for the exact sequence and rationale.
  const [resetting, setResetting] = useState(false);
  const handleResetSession = async () => {
    if (resetting) return;
    setResetting(true);
    await clearSiteStateAndReload();
    // `clearSiteStateAndReload` ends in `location.replace('/')` so
    // we never reach this line in practice. The state flip above is
    // purely UI — the button can't be clicked twice while the wipe
    // is in flight.
  };

  // Heuristic: when the captured `error.message` matches the
  // signature of the Next 14.x router-cache crash documented in the
  // file header (or has already escalated to React #185 inside our
  // fallback), promote "Reset session" to the primary CTA. The
  // ordinary "Retry" / "Go home" flow does not help on those —
  // both end up going through the same corrupt router state. The
  // first match also catches the React #185 escalation that the
  // original `<Button asChild><Link>` recovery used to produce.
  const isFrameworkCacheCrash =
    typeof error?.message === 'string' &&
    (error.message.includes("reading 'get'") ||
      error.message.includes('Minified React error #185') ||
      error.message.includes('Maximum update depth exceeded'));

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-[#07050a] text-zinc-200">
      <div className="max-w-sm w-full text-center space-y-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200">
          <span aria-hidden className="font-mono text-sm">!</span>
        </div>
        <h1 className="text-lg font-semibold text-zinc-100">
          Something went wrong loading this board.
        </h1>
        <p className="text-sm text-zinc-400">
          {isFrameworkCacheCrash
            ? 'A stale session is wedging the page. Reset to clear it.'
            : 'Tap retry to reload, or head back to the lobby.'}
        </p>
        <div className="flex items-center justify-center gap-2 pt-1 flex-wrap">
          {/* Plain <button> + window.location.reload(): no <Slot>, no
              composeRefs, no router state involvement. See file header
              for the chain that made `reset()` unsafe in this fallback. */}
          <button
            type="button"
            onClick={handleHardReload}
            className="inline-flex items-center justify-center h-9 rounded-lg px-3 text-sm font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            Retry
          </button>
          {/* Plain <a href>: full-document navigation. Replacing
              `<Button asChild><Link href="/">…</Link></Button>` here is
              the structural fix for the React #185 loop documented in
              the file header. Do not reintroduce `<Link>` until the
              upstream Next 14.x cache bug is patched. */}
          <a
            href="/"
            className="inline-flex items-center justify-center h-9 rounded-lg px-3 text-sm font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Go home
          </a>
          {/* Rescue surface — see `src/app/debug/page.tsx`. Paints
              diagnostics (device, GPU, graphics preset, wallet
              install, Privy/wagmi state, replica meta) that support
              can use to triage. Plain `<a href>` for the same reason
              as the "Go home" link above: the router cache this
              boundary catches can be corrupt, and `<Link>` would
              walk straight back into the throw. `/debug` itself
              short-circuits `<Providers>` / `<AppShell>` so it
              still paints when the normal tree is wedged. */}
          <a
            href="/debug"
            className="inline-flex items-center justify-center h-9 rounded-lg px-3 text-sm font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Open diagnostics
          </a>
          {/* Promoted to amber-highlighted primary CTA when the
              captured error matches the framework-cache pattern;
              otherwise it sits as a quieter "trouble loading?" link.
              Either way the underlying call is the same destructive
              `clearSiteStateAndReload`. */}
          <button
            type="button"
            onClick={handleResetSession}
            disabled={resetting}
            className={
              isFrameworkCacheCrash
                ? 'inline-flex items-center justify-center h-9 rounded-lg px-3 text-sm font-semibold bg-amber-500/90 text-zinc-950 hover:bg-amber-400 transition-colors disabled:opacity-60'
                : 'inline-flex items-center justify-center h-9 rounded-lg px-3 text-sm font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-60'
            }
          >
            {resetting ? 'Resetting…' : 'Reset session & reload'}
          </button>
        </div>
        <p className="text-[11px] text-zinc-500">
          Reset signs you out and clears your saved drafts and
          preferences for this site.
        </p>
        {error?.digest || replicaShort !== 'unknown' ? (
          <div className="pt-2 font-mono text-[10px] text-zinc-600 break-all">
            {error?.digest ? <>ref {error.digest}</> : null}
            {error?.digest && replicaShort !== 'unknown' ? ' · ' : null}
            {/* Replica id + region from SSR meta tags; see the same
                block in `app/global-error.tsx` for why we surface
                this in the user-facing card. */}
            {replicaShort !== 'unknown' ? (
              <>
                replica {replicaShort}
                {replicaRegion !== 'unknown' ? ` · ${replicaRegion}` : ''}
              </>
            ) : null}
          </div>
        ) : null}
        {decoded.code ? (
          // Decoded React invariant — surfaced in production now
          // that the table in `report-client-error.ts` expands the
          // numeric codes into actionable copy. Falls back to the
          // raw `error.message` (dev-only) when no React code is
          // matched.
          <pre className="mt-2 max-h-52 overflow-auto rounded-md border border-amber-500/25 bg-black/60 p-2 text-left font-mono text-[10px] leading-snug text-amber-100/90">
            {`React #${decoded.code}${decoded.decoded ? `: ${decoded.decoded}` : ''}`}
            {error?.message ? `\n\n${error.message}` : ''}
          </pre>
        ) : isDev && error?.message ? (
          <pre className="mt-2 max-h-40 overflow-auto rounded-md border border-amber-500/15 bg-black/50 p-2 text-left font-mono text-[10px] leading-snug text-amber-100/80">
            {error.message}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
